/* eslint-disable react/prop-types */
import React, { useEffect, useState, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import ContactList from '@medflex/components/stories/ContactList';
import Button from '@medflex/components/stories/Button';
import { useTranslation } from 'react-i18next';
import InputCheckbox from '@medflex/components/stories/InputCheckbox';
import { useHistory } from 'react-router-dom';
import isSelf from '@medflex/utils/isSelf';
import isSupportPatient from '@medflex/utils/isSupportPatient';
import ScrollableTabs from '@medflex/components/stories/ScrollableTabs';
import { USER_TYPES } from '@medflex/utils/constants';
import isPatientUserId from '@medflex/utils/isPatientUserId';
import { TRACKING_ACTIONS, trackEvent } from '@medflex/utils/Tracking';
import { FixedSizeList as List } from 'react-window';
import FadeContainer from '@medflex/components/stories/Fade/FadeContainer';
import Fade from '@medflex/components/stories/Fade/Fade';
import { IconClose, IconSearch } from '@medflex/components/stories/Icons';
import InputField from '@medflex/components/stories/InputField';
import matchesSearchTerm from '@medflex/utils/matchesSearchTerm';
import useIsWideScreen from '@medflex/hooks/useIsWideScreen';
import Tooltip from '@medflex/components/stories/Tooltip';
import { Role, Subtitle, Title } from '@medflex/components/stories/HighlightableInfos';
import getAreaOfActivity from '@medflex/utils/getAreaOfActivity';
import useAddressBookContacts from '../../hooks/useAddressBookContacts';
import useUserProfile from '../../hooks/useUserProfile';
import getSubtitle from '../../../utils/getSubtitle';
import useIsModal from '../../hooks/useIsModal';

const TABS = {
  ALL: 'all',
  STAFF: 'staff',
  PHYSICIANS: 'physicians',
  PATIENTS: 'patients',
};

const sortByFullNameWithTitle = (a, b) => {
  const nameA = a.user.fullNameWithTitle.toUpperCase();
  const nameB = b.user.fullNameWithTitle.toUpperCase();
  if (nameA < nameB) return -1;
  return 1;
};

const ContactsRenderer = ({ data, index, style }) => {
  const { t, selectedContacts, userProfile, handleContactClick, contacts, searchTerm } = data;

  const contact = contacts[index];

  return (
    <ContactList.Item
      index={index}
      style={style}
      className="focus:border-slate-500"
      key={contact.user.id}
      avatarSrc={isSelf(contact.user) ? null : contact.user.avatar}
      avatarInitials={isSelf(contact.user) ? null : contact.user.initials}
      avatarSize="10"
      showBorder={false}
      title={<Title contact={contact} searchTerm={searchTerm} />}
      selected={selectedContacts.map(({ user }) => user.id).includes(contact.user.id)}
      subtitle={
        <Subtitle contact={contact} searchTerm={searchTerm}>
          {getSubtitle(contact, userProfile, t)}
        </Subtitle>
      }
      role={<Role contact={contact} searchTerm={searchTerm} />}
      enabled
      onClick={() => handleContactClick({ contact })}
      hasCheckBox
    />
  );
};

const ContactsSelector = ({
  selectedContacts,
  onSelect,
  onClickContinue,
  forceMulticast,
  setMultipleContacts,
}) => {
  const history = useHistory();
  const { all } = useAddressBookContacts();
  const { t } = useTranslation('physician');
  const { userProfile } = useUserProfile();
  const { isModal } = useIsModal();
  const isWideScreen = useIsWideScreen();

  const [selectedTab, setSelectedTab] = useState(TABS.ALL);
  const [allSelected, setAllSelected] = useState(false);
  const [scrollbox, setScrollbox] = useState();
  const [searchTerm, setSearchTerm] = useState('');

  const ref = useRef();

  const tabs = [
    { key: TABS.ALL, label: t('modals.search_contact.inputs.tabs.all') },
    { key: TABS.STAFF, label: t('modals.search_contact.inputs.tabs.staff') },
    { key: TABS.PHYSICIANS, label: t('modals.search_contact.inputs.tabs.physicians') },
    { key: TABS.PATIENTS, label: t('modals.search_contact.inputs.tabs.patients') },
  ];

  const filteredContacts = useMemo(() => {
    if (all) {
      const pairedContacts = all
        .filter(contact => {
          const { user, isTeam, pairedAt, archivedAt, note } = contact;
          const subtitle = getSubtitle(contact, userProfile, t);
          const areaOfActivity = getAreaOfActivity(contact);
          return (
            !isSelf({ id: user.id }) &&
            !isSupportPatient({ id: user.id }) &&
            (isTeam ? user.termsAcceptedAt : true) &&
            (isPatientUserId(user.id) ? pairedAt : true) &&
            !archivedAt &&
            matchesSearchTerm(searchTerm, [
              user.fullNameWithTitle,
              note,
              user.workplace,
              user.workArea,
              user.dateOfBirth,
              user.jobTitle,
              subtitle,
              areaOfActivity,
            ])
          );
        })
        .sort(sortByFullNameWithTitle);

      switch (selectedTab) {
        case TABS.STAFF:
          return pairedContacts.filter(({ isTeam }) => isTeam);
        case TABS.PHYSICIANS:
          return pairedContacts.filter(({ type }) => type === USER_TYPES.PHYSICIAN);
        case TABS.PATIENTS:
          return pairedContacts.filter(({ type }) => type === USER_TYPES.PATIENT);
        default:
          return pairedContacts;
      }
    }
    return [];
  }, [all, selectedTab, searchTerm]);

  const handleCheckAll = checked => {
    if (checked) {
      setMultipleContacts(filteredContacts);
    } else {
      setMultipleContacts([]);
    }
    setAllSelected(checked);
  };

  const handleUnCheckAll = () => {
    setMultipleContacts([]);
    setAllSelected(false);
  };

  const handleContactClick = ({ contact }) => {
    onSelect({ contact });
    if (allSelected) {
      setAllSelected(false);
    }
  };

  const handleTabClick = key => {
    setSelectedTab(key);
    trackEvent({
      zone: 'multicast-message',
      action: TRACKING_ACTIONS.CLICK,
      actionText: `filter list with tab ${key}`,
    });
  };

  const handleClickContinue = e => {
    e.preventDefault();

    if (selectedContacts.length === 1 && !forceMulticast) {
      history.push(`/chat/${selectedContacts[0].id}`);
    } else {
      onClickContinue();
    }
  };

  useEffect(() => {
    if (ref && isModal && isWideScreen) {
      ref.current.focus();
    }
  }, [ref, isModal, isWideScreen]);

  return (
    <>
      <div className="mb-6 flex h-12 flex-shrink-0 bg-white">
        <ScrollableTabs active={selectedTab} tabs={tabs} onClick={handleTabClick} />
      </div>
      <InputField
        placeholder={t('modals.search_contact.inputs.searchTerm.placeholder')}
        className="flex-shrink-0 flex-grow mr-2"
        onChange={event => {
          setSearchTerm(event.target.value);
        }}
        value={searchTerm}
        innerRef={ref}
        afterInput={
          <div className="pr-4 flex justiy-center items-center cursor-pointer">
            <IconSearch size="5" className="fill-current text-slate-500" />
          </div>
        }
      />
      <div className="m-4 flex items-center justify-end text-sm">
        <p className="text-blue-500">{t('modals.search_contact.all')}</p>
        <InputCheckbox
          className="ml-2 h-4 w-4 cursor-pointer"
          checked={allSelected}
          onChange={event => handleCheckAll(event.target.checked)}
        />
      </div>
      <div className="mb-4 min-h-48 overflow-auto lg:h-96">
        <FadeContainer scrollbox={scrollbox}>
          {({ scrolledToEnd }) => (
            <>
              <List
                outerRef={setScrollbox}
                height={384}
                itemCount={filteredContacts.length}
                itemSize={90}
                itemData={{
                  contacts: filteredContacts,
                  t,
                  handleContactClick,
                  selectedContacts,
                  userProfile,
                  searchTerm,
                }}
              >
                {ContactsRenderer}
              </List>
              <Fade hidden={scrolledToEnd} />
            </>
          )}
        </FadeContainer>
      </div>
      <div className="mb-8 bg-blue-100 p-2 flex items-center text-center">
        <div className="flex-grow">
          {selectedContacts.length === 1
            ? t('modals.search_contact.contactsSelected_one', { count: selectedContacts.length })
            : t('modals.search_contact.contactsSelected_other', { count: selectedContacts.length })}
        </div>
        <div>
          {selectedContacts.length > 0 && (
            <Tooltip title={t('modals.search_contact.delete_contactsSelected_Tooltip')}>
              <Button
                className="justify-self-end"
                shape="circle"
                kind="secondary:ghost"
                onClick={handleUnCheckAll}
              >
                <IconClose className="flex items-center text-slate-400" size="3" />
              </Button>
            </Tooltip>
          )}
        </div>
      </div>
      <div className="flex lg:justify-end">
        <Button
          isDisabled={selectedContacts.length < 1}
          onClick={handleClickContinue}
          className="w-full lg:w-1/2"
        >
          <span className="inline">{t('modals.search_contact.inputs.button')}</span>
        </Button>
      </div>
    </>
  );
};

ContactsSelector.propTypes = {
  selectedContacts: PropTypes.array.isRequired,
  onSelect: PropTypes.func.isRequired,
  onClickContinue: PropTypes.func.isRequired,
  forceMulticast: PropTypes.bool,
  setMultipleContacts: PropTypes.func,
};

export default ContactsSelector;
